import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Plus, ArrowUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePermission } from '@/hooks/usePermission'
import { filterCreateItems } from './filterCreateItems'
import { CREATE_ITEMS, type CreateMenuItem } from './createRegistry'

interface CreateMenuProps {
  onSelect: (item: CreateMenuItem) => void
}

export function CreateMenu({ onSelect }: CreateMenuProps) {
  const { t } = useTranslation('create')
  const { hasPermission } = usePermission()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const groups = filterCreateItems(CREATE_ITEMS, hasPermission)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      // 下拉已 portal 到 body，DOM 上不在 wrapRef 内，所以两处都要判
      const inWrap = wrapRef.current?.contains(target)
      const inMenu = menuRef.current?.contains(target)
      if (!inWrap && !inMenu) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const show = () => { if (hideTimer.current) clearTimeout(hideTimer.current); setOpen(true) }
  const scheduleHide = () => { hideTimer.current = setTimeout(() => setOpen(false), 180) }

  const handlePick = (item: CreateMenuItem) => { setOpen(false); onSelect(item) }

  return (
    <div ref={wrapRef} className="relative" onMouseEnter={show} onMouseLeave={scheduleHide}>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 rounded-full"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('title')}
        onClick={() => setOpen((v) => !v)}
      >
        <Plus className="h-4 w-4" />
      </Button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          className="fixed right-4 top-[60px] z-[1100] w-[min(900px,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-5 shadow-lg"
        >
          <div className="mb-3 border-b border-border pb-2 text-sm font-bold text-foreground">
            {t('title')}
          </div>

          {groups.length === 0 ? (
            <p className="px-2 py-4 text-sm text-muted-foreground">{t('empty')}</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
              {groups.map((g) => (
                <div key={g.group}>
                  <div className="mx-2 mb-2 mt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t(`group.${g.group}`)}
                  </div>
                  {g.items.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.id}
                        role="menuitem"
                        type="button"
                        onClick={() => handlePick(item)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                      >
                        <Icon className="h-4 w-4 flex-none text-muted-foreground" />
                        <span>{t(item.labelKey)}</span>
                        {item.kind === 'route' && (
                          <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
